"use client";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "@/lib/client-api";
import { pcmSamples, speechChunks } from "@/lib/core-speech";

type SpeechState = { id: string; status: "loading" | "playing" | "paused" | "error"; error?: string };
export function useCoreSpeech(active: boolean, sessionId?: string) {
  const [speech, setSpeech] = useState<SpeechState | null>(null);
  const runtime = useRef<{ abort: AbortController; context: AudioContext } | null>(null);
  const stop = useCallback(() => {
    const current = runtime.current;
    runtime.current = null;
    current?.abort.abort();
    if (current && current.context.state !== "closed") void current.context.close().catch(() => {});
    setSpeech(null);
  }, []);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- Leaving Core must stop the external audio context and reflect its closed state.
  useEffect(() => { if (!active) stop(); }, [active, stop]);
  useEffect(() => () => stop(), [sessionId, stop]);

  async function play(id: string, text: string) {
    stop();
    setSpeech({ id, status: "loading" });
    let current: { abort: AbortController; context: AudioContext } | undefined;
    try {
      if (!text.trim()) throw new Error("There is no response text to read aloud.");
      if (typeof AudioContext === "undefined") throw new Error("Audio playback is unavailable in this browser.");
      const context = new AudioContext();
      current = { context, abort: new AbortController() };
      runtime.current = current;
      await context.resume();
      const signal = current.abort.signal;
      let nextStart = 0, queued = 0, finished = false;
      const chunks = speechChunks(text);
      for (const chunk of chunks) {
        signal.throwIfAborted();
        const response = await apiFetch("/api/agent/speech", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ text: chunk }), signal });
        if (!response.ok || !response.body || !response.headers.get("content-type")?.startsWith("audio/pcm")) {
          const payload = await response.json().catch(() => ({})) as { error?: string };
          throw new Error(payload.error || "OpenAI voice is unavailable. Please try again.");
        }
        const reader = response.body.getReader();
        let carry: number | undefined;
        let segmentSamples = 0;
        try {
          while (true) {
            const { value, done } = await reader.read();
            signal.throwIfAborted();
            if (done) break;
            const decoded = pcmSamples(value, carry);
            carry = decoded.carry;
            if (!decoded.samples.length) continue;
            segmentSamples += decoded.samples.length;
            const buffer = context.createBuffer(1, decoded.samples.length, 24_000);
            buffer.copyToChannel(decoded.samples as Float32Array<ArrayBuffer>, 0);
            const source = context.createBufferSource();
            source.buffer = buffer;
            source.connect(context.destination);
            nextStart = Math.max(context.currentTime + 0.08, nextStart);
            queued++;
            source.onended = () => {
              source.disconnect();
              queued--;
              if (finished && queued === 0 && runtime.current === current) stop();
            };
            source.start(nextStart);
            nextStart += buffer.duration;
            setSpeech(state => state?.id === id && state.status === "loading" ? { id, status: "playing" } : state);
          }
        } finally { await reader.cancel().catch(() => {}); reader.releaseLock(); }
        if (!segmentSamples) throw new Error("OpenAI returned no audio. Please try again.");
        if (carry !== undefined) throw new Error("The audio connection ended early. Please try again.");
      }
      finished = true;
      if (!queued && runtime.current === current) stop();
    } catch (error) {
      if (current?.abort.signal.aborted) return;
      if (current && runtime.current !== current) return;
      stop();
      setSpeech({ id, status: "error", error: error instanceof Error ? error.message : "OpenAI voice could not play. Please try again." });
    }
  }
  async function togglePause() {
    const current = runtime.current;
    if (!current) return;
    try {
      const paused = current.context.state === "suspended";
      if (paused) await current.context.resume(); else await current.context.suspend();
      if (runtime.current === current) setSpeech(state => state ? { ...state, status: paused ? "playing" : "paused" } : state);
    } catch { stop(); }
  }
  return { speech, play, stop, togglePause };
}
